import React from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import { DropdownMenu, DropdownMenuProps } from "@/shared/widgets/DropdownMenu";
import { IconName } from "@/shared/ui/Icon";
import { IconButton } from "@/shared/ui/IconButton";
import { useResendInvite } from "@/features/people/hooks/useResendInvite";
import { useRemindPeople } from "@/features/people/hooks/useRemindPeople";

interface UserOptionsDropdownProps
  extends Omit<DropdownMenuProps, "trigger" | "items"> {
  onUserRemove: () => void;
  canRemove?: boolean;
  personId: number;
  isPendingInvite?: boolean;
  isProfileIncomplete?: boolean;
  inviteSecret?: string;
  isContact?: boolean;
  options?: {
    label: string;
    onClick: () => void;
    icon: IconName;
    disabled?: boolean;
  }[];
}

function UserOptionsDropdown(props: UserOptionsDropdownProps) {
  const { t } = useTranslation();
  const {
    onUserRemove,
    canRemove = true,
    personId,
    isPendingInvite,
    isProfileIncomplete,
    isContact,
    options: additionalOptions,
    inviteSecret,
    ...rest
  } = props;

  const { handleResendInvite } = useResendInvite();
  const { handleSendProfileReminder } = useRemindPeople();

  const actionWrapper = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action();
  };

  const additionalOptionsWithActionWrapper = map(
    additionalOptions,
    (option) => ({
      ...option,
      onClick: actionWrapper(option.onClick),
    })
  );

  const handleCopyLink = (secret?: string) => {
    navigator.clipboard.writeText(
      `${import.meta.env.VITE_APP_URL}/invite/${secret}`
    );
  };

  const options = [
    {
      label: t("user.send_reminder"),
      onClick: actionWrapper(() => handleSendProfileReminder(personId)),
      icon: "Send" as IconName,
      disabled: isPendingInvite || !isProfileIncomplete || isContact,
    },
    {
      label: t("user.resend_invite"),
      onClick: actionWrapper(() => handleResendInvite({ personId })),
      icon: "Send" as IconName,
      disabled: !isPendingInvite || isContact,
    },
    {
      label: t("profile.copy_link"),
      onClick: actionWrapper(() => handleCopyLink(inviteSecret)),
      icon: "Link" as IconName,
      disabled: !isPendingInvite,
    },
    ...additionalOptionsWithActionWrapper,
    ...(canRemove
      ? [
          {
            label: t("user.remove"),
            onClick: actionWrapper(onUserRemove),
            icon: "UserRemove" as IconName,
          },
        ]
      : []),
  ].filter((option) => !option.disabled);

  return (
    <DropdownMenu
      {...rest}
      items={options}
      // sideOffset={-24}
      align="center"
      trigger={
        <IconButton icon="KebabVertical" color="black" variant="ghost" />
      }
    />
  );
}

export { UserOptionsDropdown };
